/**
 * DealCard (offline mode)
 *
 * Chat card that guides buyer and seller through an offline deal: meet at the
 * club, settle in person (cash or a payment-request link like Tikkie), then
 * confirm the handover on both sides. No money flows through the platform.
 *
 * Status flow: pending (deal agreed, item reserved) → paid (seller confirmed
 * handover) → completed (buyer confirmed receipt; item sold).
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { Button } from './Button';
import { Input } from './Input';
import { Colors, Fonts, Typography, Spacing, Radii, Shadows } from '../constants';
import { Transaction } from '../types';
import { validatePaymentLink } from '../utils/paymentLinks';

interface DealCardProps {
  transaction: Transaction;
  isBuyer: boolean;
  onConfirmHandover: () => void;
  onConfirmReceipt: () => void;
  onCancelDeal: () => void;
  onReportIssue: () => void;
  onSavePaymentLink: (link: string) => Promise<void>;
  loading?: boolean;
}

export const DealCard: React.FC<DealCardProps> = ({
  transaction,
  isBuyer,
  onConfirmHandover,
  onConfirmReceipt,
  onCancelDeal,
  onReportIssue,
  onSavePaymentLink,
  loading = false,
}) => {
  const { t } = useTranslation();

  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [savingLink, setSavingLink] = useState(false);

  // Re-validate against the whitelist before rendering a trusted pay button.
  // The DB field could be written outside setPaymentLink (e.g. a direct update
  // under the participants UPDATE policy), so never trust it as-is.
  const paymentLink =
    typeof transaction.payment_metadata?.payment_link === 'string'
      ? validatePaymentLink(transaction.payment_metadata.payment_link)
      : null;

  const handleSaveLink = async () => {
    const validated = validatePaymentLink(linkInput);
    if (!validated) {
      setLinkError(t('screens.chat.deal.paymentLinkInvalid'));
      return;
    }
    setLinkError(null);
    try {
      setSavingLink(true);
      await onSavePaymentLink(validated);
      setShowLinkInput(false);
      setLinkInput('');
    } catch (error) {
      console.error('Failed to save payment link:', error);
    } finally {
      setSavingLink(false);
    }
  };

  const handleOpenPaymentLink = () => {
    if (paymentLink) {
      Linking.openURL(paymentLink).catch((error) => {
        console.error('Failed to open payment link:', error);
      });
    }
  };

  // Completed: compact confirmation row
  if (transaction.status === 'completed') {
    return (
      <View style={styles.containerCompact}>
        <Ionicons name="checkmark-circle" size={20} color={Colors.success} />
        <Text style={styles.compactTitle}>{t('screens.chat.deal.completed')}</Text>
      </View>
    );
  }

  if (!['pending', 'paid'].includes(transaction.status)) {
    return null;
  }

  const isPending = transaction.status === 'pending';

  return (
    <View style={styles.container}>
      {/* Header: agreed price */}
      <View style={styles.header}>
        <Ionicons name="people-outline" size={18} color={Colors.textMedium} style={styles.headerIcon} />
        <Text style={styles.headerText}>
          {t('screens.chat.deal.cardTitle', { price: transaction.agreed_price.toFixed(2) })}
        </Text>
      </View>

      {/* Status + guidance */}
      {isPending ? (
        <Text style={styles.guidance}>{t('screens.chat.deal.guidance')}</Text>
      ) : (
        <Text style={styles.guidance}>
          {isBuyer
            ? t('screens.chat.deal.confirmReceiptHint')
            : t('screens.chat.deal.waitingForBuyerConfirm')}
        </Text>
      )}

      {/* Payment link: buyer sees a pay button, seller can share one */}
      {paymentLink && isBuyer && (
        <TouchableOpacity style={styles.paymentLinkButton} onPress={handleOpenPaymentLink}>
          <Ionicons name="link-outline" size={18} color={Colors.white} />
          <Text style={styles.paymentLinkButtonText}>{t('screens.chat.deal.payViaLink')}</Text>
        </TouchableOpacity>
      )}

      {!isBuyer && isPending && (
        showLinkInput ? (
          <View style={styles.linkInputSection}>
            <Input
              label={t('screens.chat.deal.paymentLinkLabel')}
              value={linkInput}
              onChangeText={(v) => {
                setLinkInput(v);
                if (linkError) setLinkError(null);
              }}
              placeholder={t('screens.chat.deal.paymentLinkPlaceholder')}
              autoCapitalize="none"
              autoCorrect={false}
              error={linkError || undefined}
            />
            <View style={styles.linkInputActions}>
              <Button
                title={t('common.buttons.cancel')}
                onPress={() => {
                  setShowLinkInput(false);
                  setLinkInput('');
                  setLinkError(null);
                }}
                variant="secondary"
                disabled={savingLink}
                style={styles.linkInputButton}
              />
              <Button
                title={t('screens.chat.deal.paymentLinkSave')}
                onPress={handleSaveLink}
                loading={savingLink}
                disabled={!linkInput.trim() || savingLink}
                style={styles.linkInputButton}
              />
            </View>
          </View>
        ) : (
          <TouchableOpacity style={styles.linkToggle} onPress={() => setShowLinkInput(true)}>
            <Ionicons name="link-outline" size={15} color={Colors.accentStrong} />
            <Text style={styles.linkToggleText}>
              {paymentLink
                ? t('screens.chat.deal.paymentLinkChange')
                : t('screens.chat.deal.paymentLinkAdd')}
            </Text>
          </TouchableOpacity>
        )
      )}

      {/* Actions */}
      <View style={styles.actions}>
        {isPending ? (
          <>
            {!isBuyer && (
              <Button
                title={t('screens.chat.deal.confirmHandover')}
                onPress={onConfirmHandover}
                loading={loading}
                disabled={loading}
                style={styles.actionButton}
              />
            )}
            <Button
              title={t('screens.chat.deal.cancelDeal')}
              onPress={onCancelDeal}
              variant="secondary"
              disabled={loading}
              style={styles.actionButton}
            />
          </>
        ) : (
          <>
            {isBuyer && (
              <Button
                title={t('screens.chat.deal.confirmReceipt')}
                onPress={onConfirmReceipt}
                loading={loading}
                disabled={loading}
                style={styles.actionButton}
              />
            )}
            <Button
              title={t('screens.chat.transactionBanner.reportIssue')}
              onPress={onReportIssue}
              variant="secondary"
              disabled={loading}
              style={styles.actionButton}
            />
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    padding: Spacing.base,
    margin: Spacing.md,
    marginTop: 0,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    ...Shadows.card,
  },
  containerCompact: {
    backgroundColor: Colors.white,
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
    padding: Spacing.sm,
    paddingHorizontal: Spacing.base,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  compactTitle: {
    ...Typography.label,
    color: Colors.success,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Spacing.sm,
  },
  headerIcon: {
    marginRight: Spacing.xs,
  },
  headerText: {
    ...Typography.cardTitle,
    color: Colors.textDark,
    flexShrink: 1,
  },
  guidance: {
    ...Typography.meta,
    color: Colors.textMedium,
    marginBottom: Spacing.md,
  },
  paymentLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.xs,
    backgroundColor: Colors.primary,
    padding: Spacing.md,
    borderRadius: Radii.control,
    marginBottom: Spacing.md,
  },
  paymentLinkButtonText: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    lineHeight: 20,
    letterSpacing: 0.2,
    color: Colors.white,
  },
  linkToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    marginBottom: Spacing.md,
  },
  linkToggleText: {
    fontFamily: Fonts.semiBold,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.accentStrong,
  },
  linkInputSection: {
    marginBottom: Spacing.md,
  },
  linkInputActions: {
    flexDirection: 'row',
    gap: Spacing.md,
  },
  linkInputButton: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    gap: Spacing.md,
    flexWrap: 'wrap',
  },
  actionButton: {
    flex: 1,
    minWidth: 140,
  },
});
